import type { Student, AIAnalysisResult, ExamQuestion } from '../types/db';

const MODEL_NAME = 'gemini-3.1-flash-lite';

const cleanJsonString = (text: string) => {
  let clean = text.trim();
  if (clean.startsWith('```json')) {
    clean = clean.replace(/^```json\s*/, '').replace(/\s*```$/, '');
  } else if (clean.startsWith('```')) {
    clean = clean.replace(/^```\s*/, '').replace(/\s*```$/, '');
  }
  return clean;
};

async function withRetry<T>(fn: () => Promise<T>, retries = 3): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      const unavailable =
        error?.message?.includes('UNAVAILABLE') ||
        error?.message?.includes('503') ||
        error?.status === 503 ||
        error?.code === 503;

      if (!unavailable || attempt === retries - 1) {
        throw error;
      }

      const waitMs = 1000 * (attempt + 1) * 2;
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
  }

  throw lastError;
}

export const analyzeStudentPerformance = async (student: Student): Promise<AIAnalysisResult> => {
  if (process.env.NODE_ENV === 'test') {
    return {
      summary: 'Uji coba: analisis dummy',
      strengths: [],
      weaknesses: [],
      recommendedFocus: [],
      studyPlan: [],
    } as AIAnalysisResult;
  }

  const { GoogleGenAI, Type } = await import('@google/genai');
  const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_API_KEY });

  const prompt = `
    Analisis profil siswa bimbingan belajar (Bimbel) berikut:
    Nama: ${student.name}
    Kelas: ${student.grade}
    Catatan Tambahan: "${student.notes || 'Tidak ada catatan.'}"
    
    Berikan analisis mendalam dalam Bahasa Indonesia (format JSON) mencakup:
    1. Ringkasan performa
    2. Kekuatan dan Kelemahan
    3. Rekomendasi fokus belajar
    4. Rencana belajar 4 minggu kedepan
  `;

  try {
    const response = await withRetry(() =>
      ai.models.generateContent({
        model: MODEL_NAME,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              summary: { type: Type.STRING },
              strengths: { type: Type.ARRAY, items: { type: Type.STRING } },
              weaknesses: { type: Type.ARRAY, items: { type: Type.STRING } },
              recommendedFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
              studyPlan: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    week: { type: Type.INTEGER },
                    topic: { type: Type.STRING },
                    activity: { type: Type.STRING },
                  },
                  required: ['week', 'topic', 'activity'],
                },
              },
            },
            required: ['summary', 'strengths', 'weaknesses', 'recommendedFocus', 'studyPlan'],
          },
        },
      })
    );

    return JSON.parse(cleanJsonString(response.text || '{}')) as AIAnalysisResult;
  } catch (error) {
    throw error;
  }
};

export const generateExamQuestions = async (
  subject: string,
  grade: string,
  topic: string,
  count: number
): Promise<Omit<ExamQuestion, 'id'>[]> => {
  if (process.env.NODE_ENV === 'test') return [];

  const { GoogleGenAI, Type } = await import('@google/genai');
  const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_API_KEY });

  const prompt = `Buatkan ${count} soal pilihan ganda (A, B, C, D) dalam Bahasa Indonesia.
  Mata Pelajaran: ${subject}
  Tingkat Kelas: ${grade}
  Topik: ${topic}
  Pastikan ada satu kunci jawaban yang benar untuk setiap soal.`;

  try {
    const response = await withRetry(() =>
      ai.models.generateContent({
        model: MODEL_NAME,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                question: { type: Type.STRING },
                options: { type: Type.ARRAY, items: { type: Type.STRING } },
                correctAnswer: { type: Type.STRING },
              },
              required: ['question', 'options', 'correctAnswer'],
            },
          },
        },
      })
    );

    return JSON.parse(cleanJsonString(response.text || '[]')) as Omit<ExamQuestion, 'id'>[];
  } catch (error) {
    throw error;
  }
};

export const structureExamFromText = async (
  rawHtmlContent: string
): Promise<Omit<ExamQuestion, 'id'>[]> => {
  if (process.env.NODE_ENV === 'test') return [];

  const { GoogleGenAI, Type } = await import('@google/genai');
  const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_API_KEY });

  const prompt = `
    Analisis konten HTML dokumen soal berikut yang diekstrak dari file Word. 
    Konten ini berisi teks pertanyaan, pilihan ganda, kunci jawaban, serta tag gambar HTML (<img src="...">) yang berisi URL gambar dari server.
    
    Tugas Anda:
    Ubah menjadi format JSON array terstruktur dengan ketentuan setiap item memiliki:
    - question: Teks pertanyaan soal.
    - image_url: Ambil URL dari atribut 'src' pada tag <img> yang terletak di dekat soal tersebut jika ada. Jika tidak ada gambar, kosongkan string-nya ("").
    - options: Array berisi pilihan jawaban (misal: A, B, C, D, E).
    - correctAnswer: Kunci jawaban yang benar (harus sama persis dengan salah satu isi options).

    Konten HTML:
    "${rawHtmlContent}"
  `;

  try {
    const response = await withRetry(() =>
      ai.models.generateContent({
        model: MODEL_NAME,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                question: { type: Type.STRING },
                image_url: { type: Type.STRING },
                options: { type: Type.ARRAY, items: { type: Type.STRING } },
                correctAnswer: { type: Type.STRING },
              },
              required: ['question', 'options', 'correctAnswer'],
            },
          },
        },
      })
    );

    return JSON.parse(cleanJsonString(response.text || '[]')) as Omit<ExamQuestion, 'id'>[];
  } catch (error) {
    throw error;
  }
};